import { useCallback, useEffect, useMemo, useState } from 'react';

import type { UUID } from '@cobliteam/common-types';

import {
  type Rule,
  RuleMode,
} from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';

import { useGetRules } from '@cobliteam/hooks/src/useEntity/useAlerts';

import { Checkbox } from '@cobliteam/base-components/src/SelectionControls/Checkbox';

import Dropdown from '@cobliteam/ui/components/Dropdown';

import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import { Button } from '../../../components';

interface MultiSelectRuleConditionsProps {
  selectedConditions: UUID[];
  selectedRule?: Rule;
  size?: 'condensed' | 'regular';
  isOpen?: boolean;
  onChange: (conditions: UUID[]) => void;
}

const MultiSelectRuleConditions = ({
  selectedConditions,
  selectedRule,
  size = 'regular',
  isOpen,
  onChange,
}: MultiSelectRuleConditionsProps) => {
  const { data: rules } = useGetRules(RuleMode.FLEET_POLICY);
  const conditions = useMemo(
    () => rules?.find((rule) => rule.id === selectedRule?.id)?.conditions || [],
    [rules, selectedRule]
  );

  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedConditions);

  const hasSameElements =
    selectedConditions?.length === draft?.length &&
    selectedConditions.every((value) => draft.includes(value));

  const handleClick = useCallback(() => {
    setDraft(selectedConditions || []);
  }, [selectedConditions]);

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  const handleSelect = useCallback(
    (conditionId: UUID) => {
      const newDraft = draft.includes(conditionId)
        ? draft.filter((d) => d !== conditionId)
        : [...draft, conditionId];
      setDraft(newDraft);
    },
    [draft, setDraft]
  );

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      onOpenChange={handleClick}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-5'>
            <Button
              variant='text'
              size='condensed'
              disabled={draft?.length === 0}
              onClick={() => setDraft([])}
            >
              Limpar seleção
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='ml-5'>
            <Dropdown.List
              heightList={300}
              heightItem={40}
              data={conditions}
              itemComponent={(item) => (
                <Checkbox
                  key={item.id}
                  label={item.name}
                  checked={draft.some((d) => d === item.id)}
                  onChange={() => handleSelect(item.id)}
                />
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-5'>
              <Button
                variant='text'
                full
                size='condensed'
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button
                size='condensed'
                full
                onClick={handleApply}
                disabled={hasSameElements}
              >
                Aplicar
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='text' startIcon={<FilterAltOutlinedIcon />}>
        Condições{' '}
        {(selectedConditions?.length || 0) > 0
          ? `(${selectedConditions?.length})`
          : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectRuleConditions;
